import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Table,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Button,
    Pagination,
} from '@mui/material';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import { useLocation, useNavigate } from 'react-router';
import HomeIcon from '@mui/icons-material/Home';
import SearchBox from '../../FlowBuilder/Common/SearchBox/index.tsx';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import CommonTable from '../../Shared/Tables/TableV1.tsx';
import { HomeActions } from '../../../Services/_actions/home.action.js';
import { Checkbox } from '@mui/material';

const ExecutionPlan: React.FC = () => {
    const [filters, setFilters] = useState({ page: 0, limit: 20, search: '' });
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [InventoryType, setInventoryType] = useState('');
    const [secondDropdownValue, setSecondDropdownValue] = useState('');
    const location = useLocation();
    const proposal = location.state?.proposal;
    const apiActions = HomeActions();
    const [tableList, setTableList] = useState<any[]>([]);
    const [dateFilter, setDateFilter] = useState('');
    const [checkedActivities, setCheckedActivities] = useState<Set<string>>(new Set());

    const dateFilterOptions = [
        { label: 'ALL', value: 'ALL' },
        { label: proposal?.created_at ? new Date(proposal.created_at).toLocaleDateString() : 'N/A', value: 'created_at' },
        { label: proposal?.updated_at ? new Date(proposal.updated_at).toLocaleDateString() : 'N/A', value: 'updated_at' },
    ];


    const handleDateFilterChange = (value: string) => {
        setDateFilter(value);
    };

    console.log("proposal", proposal)
    const firstDropdownOptions = [
        { label: 'ALL', value: 'ALL' },
        { label: 'POSTER', value: 'POSTER' },
        { label: 'STALL', value: 'STALL' },
    ];

    const handleFirstDropdownChange = (value: string) => {
        setInventoryType(value);
    };

    const handleCheckboxChange = (activityId: string) => {
        setCheckedActivities(prev => {
            const newSet = new Set(prev);
            if (newSet.has(activityId)) {
                newSet.delete(activityId);
            } else {
                newSet.add(activityId);
            }
            return newSet;
        });
    };

    const fields = [
        { label: 'Campaign Id', value: proposal?.proposal_id },
        { label: 'Campaign Name', value: proposal?.name },
        { label: 'BD Owner', value: proposal?.created_by },
        { label: 'Campaign State', value: proposal?.campaign_state },
    ].filter(f => f.value && f.value.trim() !== '');

    const rows: { label: string; value: string }[][] = [];
    for (let i = 0; i < fields.length; i += 2) {
        rows.push(fields.slice(i, i + 2));
    }

    const breadcrumbsData = [
        { label: <IconButton color="primary"><HomeIcon /></IconButton>, href: '/' },
        { label: 'Execution Plan' },
    ];

    const handleSearch = (query: string = '') => {
        setSearchTerm(query.trim().toLowerCase());
        setFilters({ ...filters, page: 0, search: query.trim().toLowerCase() });
    };

    const fetchList = async () => {
        if (!proposal?.proposal_id) return;

        try {
            const res = await apiActions.getSupplierInventoryList(proposal.proposal_id, 'true');
            if (res?.status === 200 && res.data) {
                const { inventory_activities, inventory_activity_assignment, shortlisted_inventories, shortlisted_suppliers } = res.data;

                const finalList = Object.keys(inventory_activities).map(activityId => {
                    const activity = inventory_activities[activityId];
                    const assignments = Object.values(inventory_activity_assignment).filter(
                        (a: any) => a.inventory_activity_id === Number(activityId)
                    );

                    const latestAssignment = assignments.sort((a, b) => new Date(b.activity_date).getTime() - new Date(a.activity_date).getTime())[0];

                    const inventory = shortlisted_inventories[activity.shortlisted_inventory_id];
                    const supplier = shortlisted_suppliers[inventory.shortlisted_spaces_id];

                    return {
                        activityId: activityId,
                        supplier: supplier,
                        inventory: inventory,
                        activity: activity,
                        assignment: latestAssignment,
                    };
                });

                const uniqueList = finalList.filter((item, index, self) =>
                    index === self.findIndex(t => t.activityId === item.activityId)
                );
                setTableList(uniqueList);
            } else {
                setTableList([]);
            }
        } catch (error) {
            console.error('Error fetching supplier inventory:', error);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const InventoryListColumns = useMemo(
        () => [
            {
                accessorKey: 'index++',
                header: 'S.No',
                enableSorting: false,
                size: 50,
                Cell: ({ row }) => filters.page * filters.limit + row.index + 1,
            },
            {
                header: 'Supplier Name',
                accessorKey: 'supplierName',
                Cell: ({ row }: any) => row.original?.supplier?.supplier_detail?.name || 'N/A',
            },
            {
                header: 'Inventory Type',
                accessorKey: 'inventoryType',
                Cell: ({ row }: any) => row.original?.inventory?.inventory_name || 'N/A',
            },
            {
                header: 'Image',
                accessorKey: 'image',
                Cell: () => '-',
            },
            {
                header: 'Activity Name',
                accessorKey: 'activityName',
                Cell: ({ row }: any) => row.original?.activity?.activity_type || 'N/A',
            },
            {
                header: 'Activity Date',
                accessorKey: 'activityDate',
                Cell: ({ row }: any) => row.original?.assignment?.activity_date || 'N/A',
            },
            {
                header: 'Assigned User',
                accessorKey: 'assignedUser',
                Cell: ({ row }: any) => row.original?.assignment?.assigned_to || 'N/A',
            },
            {
                header: 'ReAssign',
                accessorKey: 'reAssign',
                Cell: ({ row }: any) => {
                    const activityId = String(row.original.activityId);
                    const isChecked = checkedActivities.has(activityId);
                    return (
                        <Checkbox
                            checked={isChecked}
                            onChange={() => handleCheckboxChange(activityId)}
                            inputProps={{ 'aria-label': 'ReAssign checkbox' }}
                        />
                    );
                },
            },
            {
                header: 'ReAssigned Date',
                accessorKey: 'reAssignedDate',
                Cell: ({ row }: any) => row.original?.assignment?.reassigned_activity_date || '-',
            },
            {
                header: 'Action',
                accessorKey: 'action',
                Cell: ({ row }: any) => (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleEdit(row.original)}
                        size='small'
                    >
                        Upload Image
                    </Button>
                ),
            },
        ],
        [checkedActivities]
    );

    return (
        <Box p={2} sx={{ backgroundColor: 'white', borderRadius: 2, height: '80vh', }}>
            <DynamicBreadcrumbs
                items={breadcrumbsData}
                onBreadcrumbClick={(event, item: { label: string; href?: string }) => {
                    if (item.href) {
                        navigate(item.href);
                    }
                }}
            />
            <Grid container alignItems="center" mb={2}>
                <Grid item xs={12} md={9}>
                    <Typography variant="h5" fontWeight="bold">
                        Operations Execution Plan
                    </Typography>
                </Grid>
            </Grid>
            <Table size="small">
                <TableBody>
                    {rows.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                            {row.map((field, fieldIndex) => (
                                <React.Fragment key={fieldIndex}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{field.label}</TableCell>
                                    <TableCell>{field.value}</TableCell>
                                </React.Fragment>
                            ))}
                            {row.length < 2 && (
                                <>
                                    <TableCell />
                                    <TableCell />
                                </>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Grid
                container
                spacing={2}
                sx={{ alignItems: 'center', marginTop: '28px' }}
            >
                <Grid
                    item
                    xs={12}
                    sx={{
                        display: 'flex',
                        alignItems: 'end',
                        flexWrap: 'wrap',
                        gap: 2,
                        justifyContent: 'space-between',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <SearchBox handleSearch={handleSearch} value={filters.search || ''} />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            ml: 'auto',
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <SearchableDropdown
                                sx={{ minWidth: '194px' }}
                                label="FilterBy Inventory Type"
                                options={firstDropdownOptions}
                                value={InventoryType}
                                onChange={handleFirstDropdownChange}
                            />
                            <SearchableDropdown
                                sx={{ minWidth: '159px' }}
                                label="FilterBy Date"
                                options={dateFilterOptions}
                                value={dateFilter}
                                onChange={handleDateFilterChange}
                            />
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            <Box sx={{ paddingTop: '15px' }}>
                <CommonTable
                    columns={InventoryListColumns}
                    data={tableList}
                    enablePagination={false}
                // renderBottomToolbar={() => (
                //     <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0, margin: 0 }}>
                //         <Pagination
                //             totalCount={tableState?.totalCountCache[generateFilterKey(tableState?.filters)] || 0}
                //             rowsPerPage={tableState?.filters?.limit}
                //             page={tableState?.filters?.page}
                //             handleChangePage={handleChangePage}
                //             handleChangeRowsPerPage={handleChangeRowsPerPage}
                //         />
                //     </Box>
                // )}
                />

            </Box>
            <Box sx={{ mt: 3, display: 'flex', gap: 2, mb: 3 }}>
                <Button variant="contained" size='small'
                    color="primary" disabled={checkedActivities.size === 0}>ReAssign</Button>
                <Button variant="contained" size='small'
                    color="primary">Summary</Button>
                <Button variant="contained" size='small'
                    color="primary">Download</Button>
            </Box>

        </Box>
    );
};

export default ExecutionPlan;
